import { useRef, useEffect, useCallback } from 'react'
import { ChatMessage as ChatMessageType } from '../../types/chat'
import ChatMessage from './ChatMessage'

interface ChatMessageListProps {
  messages: ChatMessageType[]
  loading: boolean
  hasMore: boolean
  highlightQuery?: string
  searchMode?: boolean
  onLoadMore: () => Promise<void>
  onDeleteMessage?: (messageId: string) => void
  onEditMessage?: (messageId: string, content: string) => Promise<void>
  isAdmin?: boolean
}

export default function ChatMessageList({
  messages,
  loading,
  hasMore,
  highlightQuery,
  searchMode,
  onLoadMore,
  onDeleteMessage,
  onEditMessage,
  isAdmin,
}: ChatMessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const prevMessagesLengthRef = useRef(messages.length)
  const isLoadingMoreRef = useRef(false)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    // Only auto-scroll if we received new messages (not loading older ones)
    if (messages.length > prevMessagesLengthRef.current && !isLoadingMoreRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
    prevMessagesLengthRef.current = messages.length
    isLoadingMoreRef.current = false
  }, [messages.length])

  // Initial scroll to bottom
  useEffect(() => {
    if (messages.length > 0 && !loading) {
      bottomRef.current?.scrollIntoView()
    }
  }, [loading, messages.length])

  // Handle scroll to load more
  const handleScroll = useCallback(async () => {
    const container = containerRef.current
    if (!container || !hasMore || loading || isLoadingMoreRef.current) return

    // Load more when scrolled near the top
    if (container.scrollTop < 100) {
      isLoadingMoreRef.current = true
      const prevScrollHeight = container.scrollHeight
      
      await onLoadMore()
      
      // Maintain scroll position after loading older messages
      requestAnimationFrame(() => {
        if (container) {
          const newScrollHeight = container.scrollHeight
          container.scrollTop = newScrollHeight - prevScrollHeight
        }
      })
    }
  }, [hasMore, loading, onLoadMore])

  if (loading && messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-500 text-sm">Loading messages...</div>
      </div>
    )
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-400 text-sm text-center px-4">
          {searchMode ? (
            <p>No messages match your search.</p>
          ) : (
            <>
              <p>No messages yet.</p>
              <p className="mt-1">Be the first to say something!</p>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      onScroll={searchMode ? undefined : handleScroll}
      className="flex-1 overflow-y-auto px-4 py-3"
    >
      {/* Load more indicator (hidden in search mode) */}
      {!searchMode && hasMore && (
        <div className="text-center py-2">
          <button
            onClick={() => {
              isLoadingMoreRef.current = true
              onLoadMore()
            }}
            disabled={loading}
            className="text-xs text-primary hover:underline disabled:text-gray-400"
          >
            {loading ? 'Loading...' : 'Load older messages'}
          </button>
        </div>
      )}

      {/* Search results count */}
      {searchMode && (
        <div className="text-center py-1 mb-2">
          <span className="text-xs text-gray-500">
            {messages.length} result{messages.length !== 1 ? 's' : ''} found
          </span>
        </div>
      )}

      {/* Messages */}
      {messages.map((message) => (
        <ChatMessage
          key={message.id}
          message={message}
          isAdmin={searchMode ? false : isAdmin}
          highlightQuery={highlightQuery}
          onDelete={searchMode ? undefined : onDeleteMessage}
          onEdit={searchMode ? undefined : onEditMessage}
        />
      ))}

      {/* Scroll anchor */}
      <div ref={bottomRef} />
    </div>
  )
}

